import { create } from 'zustand';
import { persist } from 'zustand/middleware';

export type CartModifier = { id?: string; name: string; price: number };

export type CartItem = {
  id: string;
  dishId: string;
  name: string;
  price: number;
  photoUrl?: string | null;
  quantity: number;
  modifiers: CartModifier[];
  comment?: string;
};

type CartState = {
  establishmentId: string | null;
  items: CartItem[];
  setEstablishment: (id: string) => void;
  add: (item: Omit<CartItem, 'id' | 'quantity'>) => void;
  remove: (id: string) => void;
  inc: (id: string) => void;
  dec: (id: string) => void;
  clear: () => void;
  total: () => number;
  count: () => number;
};

function keyOf(dishId: string, mods: CartModifier[]) {
  const m = mods.map((x) => `${x.id ?? x.name}`).sort().join('|');
  return `${dishId}__${m}`;
}

export const useCart = create<CartState>()(
  persist(
    (set, get) => ({
      establishmentId: null,
      items: [],
      setEstablishment: (id) => {
        if (get().establishmentId !== id) set({ establishmentId: id, items: [] });
      },
      add: (item) => {
        const id = keyOf(item.dishId, item.modifiers);
        const items = get().items.slice();
        const idx = items.findIndex((i) => i.id === id);
        if (idx >= 0) items[idx] = { ...items[idx], quantity: items[idx].quantity + 1 };
        else items.push({ ...item, id, quantity: 1 });
        set({ items });
      },
      remove: (id) => set({ items: get().items.filter((i) => i.id !== id) }),
      inc: (id) => set({
        items: get().items.map((i) => i.id === id ? { ...i, quantity: i.quantity + 1 } : i),
      }),
      dec: (id) => {
        const items = get().items
          .map((i) => i.id === id ? { ...i, quantity: i.quantity - 1 } : i)
          .filter((i) => i.quantity > 0);
        set({ items });
      },
      clear: () => set({ items: [] }),
      total: () => get().items.reduce((s, i) => {
        const extras = i.modifiers.reduce((a, m) => a + m.price, 0);
        return s + (i.price + extras) * i.quantity;
      }, 0),
      count: () => get().items.reduce((s, i) => s + i.quantity, 0),
    }),
    { name: 'resstro_menu_cart' },
  ),
);
